const attributes = {
  trigger: 'data-quick-view-url',
  close: 'data-quick-view-close',
  content: 'data-quick-view-content',
};

// Quick view logic

class QuickView extends HTMLElement {
  constructor() {
    super();

    this.modal = this.querySelector('.quick-view');
    this.content = this.querySelector(`[${attributes.content}]`);

    this.abortController = null;
    this.activeTrigger = null;

    this.bindEvents();
  }

  bindEvents() {
    document.addEventListener('click', this.handleClick.bind(this));
    document.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  handleClick(e) {
    const trigger = e.target.closest(`[${attributes.trigger}]`);

    if (trigger) {
      const isInsideModal = this.contains(trigger);
      const url = trigger.getAttribute(attributes.trigger);

      if (!url) return;

      e.preventDefault();

      if (isInsideModal) {
        this.load(url, { preserveContent: true });
        return;
      }

      this.activeTrigger = trigger;
      this.open();
      this.load(url, { preserveContent: false });
      return;
    }

    const closeBtn = e.target.closest(`[${attributes.close}]`);
    if (closeBtn && this.contains(closeBtn)) {
      e.preventDefault();
      this.close();
    }
  }

  handleKeydown(e) {
    if (e.key === 'Escape' && this.isOpen()) {
      this.close();
    }
  }

  isOpen() {
    return this.modal.classList.contains('active');
  }

  open() {
    document.body.classList.add('quick-view-open');
    this.modal.classList.add('active');
  }

  close() {
    this.destroySliders();

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    this.modal.classList.remove('active');
    document.body.classList.remove('quick-view-open');
    this.content.classList.remove('is-loading');
    this.content.innerHTML = '';

    if (this.activeTrigger) {
      this.activeTrigger.focus();
    }
  }

  async load(url, { preserveContent = false } = {}) {
    try {
      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = new AbortController();

      if (preserveContent && this.content.innerHTML.trim() !== '') {
        this.content.classList.add('is-loading');
      } else {
        this.renderLoading();
      }

      const requestUrl = this.buildSectionUrl(url);

      const res = await fetch(requestUrl, {
        signal: this.abortController.signal,
      });

      if (!res.ok) throw new Error('Quick view request failed');

      const html = await res.text();
      const root = this.extract(html);

      if (!root) throw new Error('Product markup not found');

      this.destroySliders();

      this.content.innerHTML = '';
      this.content.appendChild(root);
      this.content.classList.remove('is-loading');

      this.init(root);
    } catch (err) {
      if (err.name === 'AbortError') return;

      console.error(err);
      this.content.classList.remove('is-loading');
      this.renderError();
    }
  }

  buildSectionUrl(url) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}section_id=quick-view-product`;
  }

  extract(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector('.product__container');
  }

  renderLoading() {
    this.content.innerHTML = `<div class="quick-view__loading">Loading...</div>`;
    this.content.classList.remove('is-loading');
  }

  renderError() {
    this.content.innerHTML = `<div class="quick-view__error">Failed to load product</div>`;
  }

  init(root) {
    this.initSlider(root);
  }

  initSlider(root) {
    const main = root.querySelector('.js-product-slider');
    const thumbs = root.querySelector('.js-product-slider-thumbs');

    if (!main) return;

    if (window.App?.initProductSliders) {
      window.App.initProductSliders(root);
      return;
    }

    if (window.jQuery && jQuery.fn.slick) {
      const $main = jQuery(main);
      const $thumbs = thumbs ? jQuery(thumbs) : null;

      if (!$main.hasClass('slick-initialized')) {
        $main.slick({
          slidesToShow: 1,
          arrows: true,
          asNavFor: thumbs || null,
        });
      }

      if ($thumbs && !$thumbs.hasClass('slick-initialized')) {
        $thumbs.slick({
          slidesToShow: 4,
          asNavFor: main,
          focusOnSelect: true,
        });
      }
    }
  }

  destroySliders() {
    if (!window.jQuery || !jQuery.fn.slick) return;

    this.content.querySelectorAll('.slick-initialized').forEach(el => {
      jQuery(el).slick('unslick');
    });
  }
}

// Injecting quick view to searchspring product cards

class SearchspringQuickViewTriggers {
  constructor() {
    this.cardSelector = '.ss__result__inner.product-item';
    this.resultsSelector = '.collection__inner, .ss-recommendations';
    this.triggerSelector = '.product-item__quick-view-trigger';
  }

  init() {
    const roots = document.querySelectorAll(this.resultsSelector);
    if (!roots.length) return;

    roots.forEach(root => {
      // prevent duplicate observers
      if (root.dataset.qvObserved === 'true') return;
      root.dataset.qvObserved = 'true';

      // initial injection
      this.injectTriggers(root);

      const observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          mutation.addedNodes.forEach(node => {
            if (!(node instanceof HTMLElement)) return;

            if (node.matches(this.cardSelector)) {
              this.injectTrigger(node);
              return;
            }

            node.querySelectorAll(this.cardSelector).forEach(card => {
              this.injectTrigger(card);
            });
          });
        });
      });

      observer.observe(root, {
        childList: true,
        subtree: true,
      });
    });
  }

  injectTriggers(scope) {
    scope.querySelectorAll(this.cardSelector).forEach(card => {
      this.injectTrigger(card);
    });
  }

  injectTrigger(card) {
    if (card.querySelector(this.triggerSelector)) return;

    const link = card.querySelector('a[href*="/products/"]');
    if (!link) return;

    const rawUrl = link.getAttribute('href');
    if (!rawUrl) return;

    const url = new URL(rawUrl, window.location.origin).pathname;

    const container = card.querySelector('.product-item__content');
    if (!container) return;

    const style = document.body.dataset.quickViewStyle || 'text';
    const trigger = document.createElement('div');
    trigger.className = `product-item__quick-view-trigger ${style === 'text' ? 'product-item__quick-view-trigger--text' : 'product-item__quick-view-trigger--icon'}`;
    trigger.setAttribute('data-quick-view-url', url);
    if (style === 'text') {
      trigger.textContent = 'Quick Add';
    } else {
      trigger.innerHTML = `
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M11.5304 5.20968H7.43183V1.11107C7.43183 0.497438 6.93439 0 6.32076 0C5.70712 0 5.20968 0.497438 5.20968 1.11107V5.20968H1.11107C0.497438 5.20968 0 5.70712 0 6.32076C0 6.93439 0.497438 7.43183 1.11107 7.43183H5.20968V11.5304C5.20968 12.1441 5.70712 12.6415 6.32076 12.6415C6.93439 12.6415 7.43183 12.1441 7.43183 11.5304V7.43183H11.5304C12.1441 7.43183 12.6415 6.93439 12.6415 6.32076C12.6415 5.70712 12.1441 5.20968 11.5304 5.20968Z" fill="white"/>
        </svg>
      `;
    }

    container.insertAdjacentElement('afterend', trigger);
  }
}

function shouldInitSearchspringQuickView() {
  const body = document.body;

  return (
    body.classList.contains('template-collection') || body.classList.contains('template-product')
  );
}

if (!customElements.get('quick-view')) {
  customElements.define('quick-view', QuickView);
}

document.addEventListener('DOMContentLoaded', () => {
  if (!shouldInitSearchspringQuickView()) return;

  new SearchspringQuickViewTriggers().init();
});
